import {
  BarChart3,
  Inbox,
  LayoutDashboard,
  LogOut,
  MailCheck,
  MessageSquareReply,
  Send,
  Settings,
  Users,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@/components/providers/auth-context';
import { cn } from '@/lib/cn';

import { LocationSwitcher } from './location-switcher';

interface NavItem {
  href: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const NAV: NavItem[] = [
  { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/campaigns', label: 'Campaigns', icon: Send },
  { href: '/mailboxes', label: 'Mailboxes', icon: Inbox },
  { href: '/leads', label: 'Leads', icon: Users },
  { href: '/replies', label: 'Inbox', icon: MessageSquareReply },
  { href: '/sent', label: 'Sent', icon: MailCheck },
  { href: '/analytics', label: 'Analytics', icon: BarChart3 },
  { href: '/settings', label: 'Settings', icon: Settings },
];

export function Sidebar(): JSX.Element {
  const { operator, signOut } = useAuth();
  return (
    <aside className="w-56 flex-shrink-0 border-r border-mb-border bg-mb-surface-elev flex flex-col">
      <div className="px-4 py-4 border-b border-mb-border">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-mb-accent flex items-center justify-center text-mb-accent-fg font-display font-bold text-lg">
            M
          </div>
          <div className="font-display font-semibold text-mb-text">MailingBees</div>
        </div>
        {operator?.is_platform_admin ? (
          <div className="mt-3">
            <LocationSwitcher
              currentLocationId={operator.current_location_id}
              currentLocationName={
                operator.memberships.find(
                  (m) => m.location_id === operator.current_location_id,
                )?.location_name ?? null
              }
            />
          </div>
        ) : null}
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        {NAV.map(({ href, label, icon: Icon }) => (
          <NavLink
            key={href}
            to={href}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 px-4 py-2 text-sm text-mb-text-muted hover:bg-mb-surface hover:text-mb-text transition-colors',
                isActive && 'bg-mb-accent-soft text-mb-accent-press font-medium',
              )
            }
          >
            <Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-mb-border px-4 py-3">
        <div className="text-xs text-mb-text-muted truncate mb-2">
          {operator?.email ?? '—'}
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          className="inline-flex items-center gap-2 text-xs text-mb-text-muted hover:text-mb-text"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden />
          Sign out
        </button>
      </div>
    </aside>
  );
}
